import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import DashboardCards from "../components/DashboardCards";
import DataTable from "../components/DataTable";
import ChatbotButton from "../components/ChatbotButton";
import ChatbotModal from "../components/ChatbotModal";
import Finance from "./Finance";


function Dashboard() {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  // Instead of viewDashboard and showTravelForm, use one state to manage views:
  const [activeView, setActiveView] = useState("dashboard"); // "dashboard", "projectDetails", "travelForm"
  const [showChatbot, setShowChatbot] = useState(false);

  const navigate = useNavigate();
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  const user = JSON.parse(localStorage.getItem("user"));
  // console.log(user)
  const role = user?.role || "Viewer";

  const [formData, setFormData] = useState({
    employee_id: user?.eid || "",
    employee_name: `${user?.fname || ""} ${user?.lname || ""}`.trim(),
    project_name: "",
    source: "",
    destination: "",
    price: "",
    date: "",
    status: 1,
  });

  useEffect(() => {
    if (!user) {
      console.warn("User not found in localStorage. Redirecting to login.");
      navigate("/login");
    }
  }, [navigate, user]);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${baseUrl}/projects`);
      // console.log("hitting")
      // console.log("Fetched projects:", res.data);
      setProjects(res.data || []);
      setSelected(res.data?.[0] || null);
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTravelSubmit = async () => {
    try {
      await axios.post(`${baseUrl}/travel_request`, formData);
      alert("Travel request submitted successfully!");
      setActiveView("dashboard");
    } catch (err) {
      console.error("Error submitting travel request:", err);
      alert("Submission failed!");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header toggleSidebar={toggleSidebar} />
      <Sidebar open={sidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeView === "dashboard" && (
          <>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Welcome to Dashboard
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {role !== "Employee" && role !== "Finance Manager" && (
                <div
                  onClick={() => setActiveView("projectDetails")}
                  className="cursor-pointer bg-white rounded-lg shadow hover:shadow-md transition p-6 text-center"
                >
                  <h3 className="text-xl font-semibold mb-2">View Project Details</h3>
                  <p className="text-gray-600">See project summaries and analytics</p>
                </div>
              )}

              {(role === "Admin" || role === "Project Manager") && (
                <div
                  onClick={() => navigate("/submit-project")}
                  className="cursor-pointer bg-white rounded-lg shadow hover:shadow-md transition p-6 text-center"
                >
                  <h3 className="text-xl font-semibold mb-2">Add Project</h3>
                  <p className="text-gray-600">Start a new project submission</p>
                </div>
              )}

              {role === "Employee" && (
                <div
                  onClick={() => setActiveView("travelForm")}
                  className="cursor-pointer bg-white rounded-lg shadow hover:shadow-md transition p-6 text-center"
                >
                  <h3 className="text-xl font-semibold mb-2">Travel Request Form</h3>
                  <p className="text-gray-600">Submit travel details for approval</p>
                </div>
              )}

              {role === "Finance Manager" && (
                <div
                  onClick={() => setActiveView("finance")}
                  className="cursor-pointer bg-white rounded-lg shadow hover:shadow-md transition p-6 text-center"
                >
                  <h3 className="text-xl font-semibold mb-2">Approve Travel Request</h3>
                  <p className="text-gray-600">Review and approve submitted travel requests</p>
                </div>
              )}
            </div>


            {/* Show chatbot button only if role is not Employee */}
            {/* {role !== "Employee" && role !== "Finance Manager" && ( */}
            <>
              <ChatbotButton onClick={() => setShowChatbot(true)} />
              {showChatbot && <ChatbotModal onClose={() => setShowChatbot(false)} />}
            </>



          </>
        )}

        {activeView === "travelForm" && (
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-2xl font-semibold text-gray-800">Travel Request Form</h3>
              <button
                onClick={() => setActiveView("dashboard")}
                className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-1.5 px-3 rounded"
              >
                Back
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Employee ID</label>
                <input
                  type="text"
                  name="employee_id"
                  value={formData.employee_id}
                  readOnly
                  className="p-2 w-full border border-gray-300 rounded bg-gray-100 text-sm"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Employee Name</label>
                <input
                  type="text"
                  name="employee_name"
                  value={formData.employee_name}
                  readOnly
                  className="p-2 w-full border border-gray-300 rounded bg-gray-100 text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block font-medium text-gray-700 mb-1">Project</label>
                <select
                  name="project_name"
                  value={formData.project_name}
                  onChange={handleInputChange}
                  className="p-2 w-full border border-gray-300 rounded text-sm"
                >
                  <option value="">Select Project</option>
                  {projects.map((proj) => (
                    <option key={proj.project_id} value={proj.project_id}>
                      {proj.project_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Source</label>
                <input
                  type="text"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                  placeholder="Enter source location"
                  className="p-2 w-full border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Destination</label>
                <input
                  type="text"
                  name="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  placeholder="Enter destination location"
                  className="p-2 w-full border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  className="p-2 w-full border border-gray-300 rounded text-sm"
                />
              </div>

              <div>
                <label className="block font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  className="p-2 w-full border border-gray-300 rounded text-sm"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block font-medium text-gray-700 mb-1">Status</label>
                <input
                  type="number"
                  name="status"
                  value={formData.status}
                  readOnly
                  className="p-2 w-full border border-gray-300 rounded bg-gray-100 text-sm"
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-3 text-sm">
              <button
                onClick={() => setActiveView("dashboard")}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleTravelSubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
              >
                Submit Request
              </button>
            </div>
          </div>
        )}

        {activeView === "projectDetails" && (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800 pl-5">Project Details</h2>
              <button
                onClick={() => setActiveView("dashboard")}
                className="text-sm bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded"
              >
                Back to Dashboard
              </button>
            </div>

            {role !== "Viewer" && selected && <DashboardCards financials={selected} />}
            <DataTable projects={projects} />
          </>
        )}

        {activeView === "finance" && (
          <Finance baseUrl={baseUrl} setActiveView={setActiveView} />
        )}

      </div>
    </div>
  );
}

export default Dashboard;
